import { zodResolver } from '@hookform/resolvers/zod';
import { Button, Col, Flex, Icons } from '@jcm/design-system';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import CustomAlert from '../../../../../../../shared/components/customAlert/customAlert';
import ModalContainer from '../../../../components/modalContainer/ModalContainer';
import ModalConcluirObrigacao from '../../../../components/obrigacoes/ModalConcluirObrigacao/ModalConcluirObrigacao';
import ObrigacoesService from '../../../../services/Obrigacoes/Obrigacoes.service';
import { TEtapa } from '../../../../ts/types/ObrigacaoEtapa/Etapas/Etapa';
import { CriarObrigacaoPayload, TObrigacao } from '../../../../ts/types/ObrigacaoEtapa/Obrigacoes/Obrigacoes';
import { transformObrigacaoToPayload } from '../../../../ts/types/serializers';
import {
	obrigacaoEstaConcluida,
	verificarAprovadorPrincipal,
	verificarResponsavelPrincipal,
} from '../../../../utils/determinarAcessoObrigacoes';
import todasEtapasConcluidas from '../../../../utils/obrigacao/todasEtapasConcluidas';
import { EditarObrigacaoZodSchema } from '../../../../zod/validations/EditarObrigacaoZodSchema';
import FormEtapas from '../../FormEtapas';
import FormObrigacoes from '../../FormObrigacoes/FormObrigacoes';
import style from './index.module.scss';

interface AcoesProps {
	onVerEtapasClick: () => void;
	onAdicionarEtapasClick?: () => void;
	disabled?: boolean;
	obrigacao: TObrigacao;
	empresaId: string | number;
	concluida: boolean;
	onObrigacaoChange: (obrigacaoAtualizada: TObrigacao) => void;
	onAttachClick: () => void;
	atualizarListagemDeObrigacoes: () => void;
}

const Acoes: React.FC<AcoesProps> = ({
	onVerEtapasClick,
	onAdicionarEtapasClick,
	disabled,
	obrigacao,
	empresaId,
	onObrigacaoChange,
	onAttachClick,
	atualizarListagemDeObrigacoes,
}) => {
	const [isModalVisible, setModalVisible] = useState(false);
	const [modalContent, setModalContent] = useState<React.ReactNode>(null);
	const [visibilidadeModalObrigacao, setVisibilidadeModalObrigacao] = useState(false);
	const obrigacoesService = new ObrigacoesService();
	const [loading, setLoading] = useState(false);
	const [alert, setAlert] = useState<{ type: 'success' | 'error'; content: string; title?: string } | null>(null);
	const [houveAlteracao, setHouveAlteracao] = useState(false);
	const [modalEtapaConteudo, setModalEtapaConteudo] = useState<React.ReactNode>();
	const [visibilidadeModalEtapa, setVisibilidadeModalEtapa] = useState<boolean>(false);

	const etapas = obrigacao.children;

	const obrigacaoSerializada = transformObrigacaoToPayload(obrigacao);

	const ehAprovadorPrincipal = verificarAprovadorPrincipal(
		obrigacaoSerializada.aprovadorPrincipal ?? 0,
		obrigacaoSerializada.aprovadoresSelecionados ?? [],
	);
	const ehResponsavelPrincipal = verificarResponsavelPrincipal(
		obrigacaoSerializada.responsavelPrincipal ?? 0,
		obrigacaoSerializada.responsaveisSelecionados ?? [],
	);

	const createDefaultValues = (initialData: CriarObrigacaoPayload) => ({
		...initialData,
		aprovadorPrincipal: initialData.aprovadorPrincipal ?? 0,
		responsavelPrincipal: initialData.responsavelPrincipal ?? 0,
		listaEmailSelecionados: initialData.listaEmailSelecionados ?? [],
		listaEmailSomenteAtrasoSelecionados: initialData.listaEmailSomenteAtrasoSelecionados ?? [],
		dataConclusaoObrigacao: new Date(),
		dataHoje: new Date(),
		diaUtil: initialData.diaUtil ? String(initialData.diaUtil) : undefined,
	});

	const defaultValues = createDefaultValues(obrigacaoSerializada);
	type FormValues = z.infer<typeof EditarObrigacaoZodSchema>;

	const { control, setValue, getValues, unregister, reset } = useForm<FormValues>({
		resolver: zodResolver(EditarObrigacaoZodSchema),
		defaultValues,
	});

	useEffect(() => {
		reset(createDefaultValues(obrigacaoSerializada));
	}, [obrigacao, reset]);

	const handleSubmitObrigacao = async (data: CriarObrigacaoPayload) => {
		setLoading(true);

		try {
			const response = await obrigacoesService.atualizarObrigacao(empresaId, obrigacao.id, data);
			onObrigacaoChange(response.data as TObrigacao);

			setAlert({ type: 'success', content: 'Obrigação concluída com sucesso!', title: 'Sucesso' });

			handleCloseModal();
		} catch {
			setAlert({ type: 'error', content: 'Erro ao concluir a obrigação. Tente novamente!', title: 'Erro' });
		} finally {
			setLoading(false);
		}
	};

	const handleOpenModal = () => {
		setModalContent(
			<FormObrigacoes
				obrigacao={obrigacao}
				isEdit={true}
				initialData={transformObrigacaoToPayload(obrigacao)}
				empresaId={empresaId}
				obrigacaoId={obrigacao.id}
				onClose={() => {
					handleCloseModal();
				}}
				onSave={() => {
					setModalVisible(false);
					atualizarListagemDeObrigacoes();
				}}
				onObrigacaoChange={onObrigacaoChange}
				onAttachClick={onAttachClick}
				onAtualizar={atualizarListagemDeObrigacoes}
				onAlteracao={handleAlteracaoDetectada}
			/>,
		);
		setHouveAlteracao(false);
		setModalVisible(true);
	};

	const mostrarModalEtapa = (content: React.ReactNode) => {
		setModalEtapaConteudo(content);
		setVisibilidadeModalEtapa(true);
	};

	const fecharModalEtapa = () => {
		setVisibilidadeModalEtapa(false);
	};

	const handleCloseModal = () => {
		if (houveAlteracao) {
			atualizarListagemDeObrigacoes();
		}
		setModalVisible(false);
	};

	const handleAlteracaoDetectada = (alterado: boolean) => {
		if (alterado) {
			setHouveAlteracao(true);
		}
	};

	return (
		<Col span={24}>
			<Flex justify='space-between' align='middle'>
				<Flex gap={8}>
					<Button variant='default' type='outlined' onClick={handleOpenModal}>
						Editar
					</Button>

					{todasEtapasConcluidas((obrigacao.children as TEtapa[]) ?? []) && (
						<>
							{((!obrigacao.concluida && ehResponsavelPrincipal) || (obrigacao.concluida && ehAprovadorPrincipal)) &&
								obrigacao.estadoStatusAprovada !== 2 && (
									<Button
										type='filled'
										variant='default'
										onClick={() => {
											setVisibilidadeModalObrigacao(true);
										}}
										loading={loading}
									>
										{ehAprovadorPrincipal && ehResponsavelPrincipal && obrigacao.estadoStatusAprovada === 1
											? 'Analisar a conclusão da obrigação'
											: ehAprovadorPrincipal && !ehResponsavelPrincipal
												? 'Analisar a conclusão da obrigação'
												: ehAprovadorPrincipal && ehResponsavelPrincipal
													? 'Concluir Obrigação'
													: 'Concluir obrigação'}
									</Button>
								)}
						</>
					)}
				</Flex>

				<Flex align='center' className={style.textColor}>
					{!obrigacaoEstaConcluida(obrigacao.estadoStatusAprovada) && (
						<Flex align='center' onClick={onAdicionarEtapasClick} className={style.cursorPointer}>
							<Button
								type='text'
								variant='default'
								className={style.botoesAcoesEtapas}
								onClick={() => {
									mostrarModalEtapa(
										<FormEtapas
											obrigacao={obrigacao}
											empresaId={empresaId}
											onClose={() => {
												fecharModalEtapa();
											}}
											onSave={() => {
												setModalVisible(false);
												atualizarListagemDeObrigacoes();
											}}
											onObrigacaoChange={onObrigacaoChange}
										/>,
									);
								}}
							>
								<Flex align='center'>
									Adicionar etapas
									<Icons.Add className={style.iconSize} />
								</Flex>
							</Button>
						</Flex>
					)}

					{!!etapas?.length && (
						<Flex
							align='center'
							onClick={!disabled ? onVerEtapasClick : undefined}
							className={`${style.pointer} ${disabled && style.disabledCursor}`}
						>
							<Button type='text' variant='default' className={style.botoesAcoesEtapas}>
								<Flex align='center'>
									Ver etapas
									<Icons.KeyboardArrowDown
										variant='outlined'
										className={`${style.iconSize} ${disabled ? style.disabledColor : style.defaultColor}`}
									/>
								</Flex>
							</Button>
						</Flex>
					)}
				</Flex>
			</Flex>

			<ModalContainer className={style.modalFormEditContainer} isVisible={isModalVisible} onClose={handleCloseModal}>
				{modalContent}
			</ModalContainer>

			<ModalConcluirObrigacao
				isVisible={visibilidadeModalObrigacao}
				onClose={() => setVisibilidadeModalObrigacao(false)}
				getValues={getValues}
				setValue={setValue}
				control={control}
				unregister={unregister}
				usuarioAprovador={ehAprovadorPrincipal}
				usuarioResponsavel={ehResponsavelPrincipal}
				handleSubmit={handleSubmitObrigacao}
			/>

			<ModalContainer
				isVisible={visibilidadeModalEtapa}
				onClose={fecharModalEtapa}
				className={`form-obrigacao ${style.modalFormContainer}`}
				key={isModalVisible ? 'visible' : 'hidden'}
			>
				{modalEtapaConteudo}
			</ModalContainer>

			{alert && <CustomAlert type={alert.type} content={alert.content} title={alert.title} />}
		</Col>
	);
};

export default Acoes;
